import { useMemo } from "react";
import { useLocation } from "react-router-dom";

/**
 * Extracts the first path segment after a base path.
 * For example, given pathname="/stores/123/order-management/outbound-labels/batch-history"
 * and basePath="/stores/123/order-management/outbound-labels", returns "batch-history".
 */
function getFirstSubPathSegment(
  pathname: string,
  basePath: string,
): string | null {
  if (!pathname.startsWith(basePath + "/")) {
    return null;
  }
  const remainder = pathname.slice(basePath.length + 1);
  return remainder.split("/")[0] || null;
}

/**
 * Hook to determine if the current route is a child of a given base path,
 * excluding certain route segments.
 *
 * Used to auto-expand navigation sections when navigating directly to a child route.
 *
 * @param basePath - The base URL path to check against
 * @param excludedSubPaths - Array of first-level sub-paths that should NOT count as children
 * @returns true if current route is under basePath and not in excludedSubPaths
 */
export function useIsChildRouteActive(
  basePath: string,
  excludedSubPaths: string[],
): boolean {
  const { pathname } = useLocation();

  return useMemo(() => {
    const subPath = getFirstSubPathSegment(pathname, basePath);
    if (!subPath) {
      return false;
    }
    return !excludedSubPaths.includes(subPath);
  }, [pathname, basePath, excludedSubPaths]);
}
